import React, { useState, useRef } from 'react';
import { Stage, Layer, Rect, Text, Group } from 'react-konva';
import { getRowLabel } from '../utils/seatNaming';
import './VenueVisualDesigner.css';

const VenueVisualDesigner = ({ venue, onSave, onCancel }) => {
  const [sections, setSections] = useState(() => {
    return (venue.sections || []).map((section, index) => ({
      ...section,
      position: section.position || {
        x: 100 + (index % 3) * 350,
        y: 150 + Math.floor(index / 3) * 150
      }
    }));
  });
  
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [stageConfig] = useState({
    width: 1200,
    height: 800,
    scale: 1
  });

  const [stagePosition] = useState({ x: 600, y: 50 });

  const updateRowSeats = (sectionId, rowIndex, seats) => {
    const newSections = sections.map(s => {
      if (s.id === sectionId) {
        const rowSeats = s.rowSeats || {};
        
        if (seats === s.seatsPerRow) {
          const { [rowIndex]: removed, ...rest } = rowSeats;
          return { ...s, rowSeats: rest };
        }
        
        return {
          ...s,
          rowSeats: { ...rowSeats, [rowIndex]: Math.max(1, seats) }
        };
      }
      return s;
    });
    setSections(newSections);
  };

  const resetRowSeats = (sectionId) => {
    const newSections = sections.map(s =>
      s.id === sectionId ? { ...s, rowSeats: {} } : s
    );
    setSections(newSections);
  };

  const handleDragEnd = (sectionId, e) => {
    const newSections = sections.map(s => {
      if (s.id === sectionId) {
        return {
          ...s,
          position: {
            x: e.target.x(),
            y: e.target.y()
          }
        };
      }
      return s;
    });
    setSections(newSections);
  };

  const renderSection = (section) => {
    const rowCount = section.endRow - section.startRow + 1;
    const width = section.seatsPerRow * 25;
    const height = rowCount * 20;
    const x = section.position?.x || 100;
    const y = section.position?.y || 100;

    return (
      <Group
        key={section.id}
        x={x}
        y={y}
        draggable
        onDragEnd={(e) => handleDragEnd(section.id, e)}
        onClick={() => setSelectedSectionId(section.id)}
      >
        <Rect
          width={width}
          height={height}
          fill={selectedSectionId === section.id ? '#667eea' : '#e0e7ff'}
          stroke="#667eea"
          strokeWidth={2}
          cornerRadius={5}
        />
        
        <Text
          text={section.name}
          fontSize={14}
          fontStyle="bold"
          fill="#2c3e50"
          width={width}
          align="center"
          y={5}
        />

        <Text
          text={`${rowCount} sıra × ${section.seatsPerRow} koltuk`}
          fontSize={11}
          fill="#6c757d"
          width={width}
          align="center"
          y={height - 20}
        />
      </Group>
    );
  };

    const handleSave = () => {
    const updatedVenue = {
        ...venue,
        sections: sections.map(s => ({
        id: s.id,
        name: s.name,
        type: s.type,
        startRow: s.startRow,
        endRow: s.endRow,
        seatsPerRow: s.seatsPerRow,
        rowPrefix: s.rowPrefix,
        namingMode: s.namingMode || 0,
        position: s.position || { x: 100, y: 100 },
        rowSeats: s.rowSeats || {}  // ← null değil {} gönderin
        }))
    };
    
    console.log('💾 VenueVisualDesigner KAYDET:', updatedVenue.sections);
    onSave(updatedVenue);
    };

  return (
    <div className="visual-designer">
      <div className="designer-header">
        <h2>🎨 Görsel Salon Tasarımcısı</h2>
        <div className="header-actions">
          <button onClick={onCancel} className="cancel-btn">İptal</button>
          <button onClick={handleSave} className="save-btn">💾 Kaydet</button>
        </div>
      </div>

      <div className="designer-container">
        {/* Sol Panel */}
        <div className="sections-panel">
          <h3>📋 Bölümler</h3>
          <div className="sections-list">
            {sections.map(section => (
              <div
                key={section.id}
                className={`section-item ${selectedSectionId === section.id ? 'selected' : ''}`}
                onClick={() => setSelectedSectionId(section.id)}
              >
                <strong>{section.name}</strong>
                <span>{section.endRow - section.startRow + 1} sıra</span>
              </div>
            ))}
          </div>
        </div>

        {/* Orta: Canvas */}
        <div className="canvas-container">
          <div className="canvas-tools">
            <span>💡 İpucu: Bölümleri sürükleyerek yerleştirin</span>
          </div>

          <Stage
            width={stageConfig.width}
            height={stageConfig.height}
            scaleX={stageConfig.scale}
            scaleY={stageConfig.scale}
          >
            <Layer>
              {/* Sahne */}
              <Group x={stagePosition.x} y={stagePosition.y}>
                <Rect
                  width={300}
                  height={60}
                  fill="#ffd700"
                  stroke="#f39c12"
                  strokeWidth={2}
                  cornerRadius={5}
                />
                <Text
                  text="🎬 SAHNE"
                  fontSize={20}
                  fontStyle="bold"
                  fill="#2c3e50"
                  width={300}
                  align="center"
                  y={20}
                />
              </Group>

              {sections.map(renderSection)}
            </Layer>
          </Stage>
        </div>

        {/* Sağ Panel */}
        {selectedSectionId && (
          <div className="properties-panel">
            <h3>⚙️ Bölüm Özellikleri</h3>
            {(() => {
              const section = sections.find(s => s.id === selectedSectionId);
              const rowCount = section.endRow - section.startRow + 1;
              
              return (
                <div className="properties-form">
                  <div className="property-item">
                    <label>Bölüm Adı:</label>
                    <strong>{section.name}</strong>
                  </div>
                  
                  <div className="property-item">
                    <label>Sıra Sayısı:</label>
                    <strong>{rowCount}</strong>
                  </div>

                  <div className="property-item">
                    <label>Varsayılan Koltuk/Sıra:</label>
                    <input
                      type="number"
                      value={section.seatsPerRow}
                      onChange={(e) => {
                        const newSections = sections.map(s =>
                          s.id === selectedSectionId
                            ? { ...s, seatsPerRow: parseInt(e.target.value) || 1 }
                            : s
                        );
                        setSections(newSections);
                      }}
                      min="1"
                    />
                    <small className="hint">Tüm sıralar için varsayılan değer</small>
                  </div>

                  <hr />

                  {/* Sıra Bazında Düzenleme */}
                  <div className="property-item">
                    <label>🔧 Sıra Bazında Düzenleme:</label>
                    <div className="row-seats-editor">
                      {Array.from({ length: rowCount }, (_, index) => {
                        const rowLabel = getRowLabel(section, index);
                        const currentSeats = section.rowSeats?.[index] || section.seatsPerRow;
                        
                        return (
                          <div key={index} className="row-seat-item">
                            <span className="row-label">{rowLabel}</span>
                            <div className="seat-controls">
                              <button
                                type="button"
                                onClick={() => updateRowSeats(section.id, index, currentSeats - 1)}
                                className="seat-btn minus"
                                disabled={currentSeats <= 1}
                              >
                                −
                              </button>
                              <input
                                type="number"
                                value={currentSeats}
                                onChange={(e) => updateRowSeats(section.id, index, parseInt(e.target.value) || 1)}
                                min="1"
                                className="seat-input"
                              />
                              <button
                                type="button"
                                onClick={() => updateRowSeats(section.id, index, currentSeats + 1)}
                                className="seat-btn plus"
                              >
                                +
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => resetRowSeats(section.id)}
                      className="reset-btn"
                    >
                      🔄 Tümünü Varsayılana Döndür
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default VenueVisualDesigner;